
import React, { useState, useCallback, useEffect } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import type { CarouselApi } from '@/components/ui/carousel';
import type { Screenshot } from './types';
import ScreenshotCard from './ScreenshotCard';
import NavigationIndicators from './NavigationIndicators';

interface TabletScreenshotCarouselProps {
  screenshots: Screenshot[];
}

const TabletScreenshotCarousel: React.FC<TabletScreenshotCarouselProps> = ({ screenshots }) => {
  const [api, setApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const totalSlides = Math.ceil(screenshots.length / 2);

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
  }, []);

  const handleSlideClick = useCallback((index: number) => {
    if (!api) return;
    api.scrollTo(index);
  }, [api]);

  useEffect(() => {
    if (!api) return;
    
    onSelect(api);
    api.on("select", onSelect);
    
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <div className="hidden sm:block lg:hidden">
      <Carousel 
        className="w-full" 
        setApi={setApi}
        opts={{
          align: "start",
          loop: false,
          dragFree: true,
        }}
      >
        <CarouselContent>
          {/* First carousel view - screenshots 0-1 */}
          <CarouselItem>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {screenshots.slice(0, 2).map((screenshot, index) => (
                <ScreenshotCard 
                  key={index} 
                  screenshot={screenshot} 
                  isMobile={false}
                />
              ))}
            </div>
          </CarouselItem>
          
          {/* Second carousel view - screenshots 2-3 */}
          <CarouselItem>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {screenshots.slice(2, 4).map((screenshot, index) => (
                <ScreenshotCard 
                  key={index + 2} 
                  screenshot={screenshot} 
                  isMobile={false}
                />
              ))}
            </div>
          </CarouselItem>
          
          {/* Third carousel view - screenshots 4-5 */}
          <CarouselItem>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {screenshots.slice(4, 6).map((screenshot, index) => (
                <ScreenshotCard 
                  key={index + 4} 
                  screenshot={screenshot} 
                  isMobile={false}
                />
              ))}
            </div>
          </CarouselItem>
        </CarouselContent>
        
        <CarouselPrevious className="left-2 md:-left-12" />
        <CarouselNext className="right-2 md:-right-12" />
      </Carousel>
      
      <NavigationIndicators 
        totalSlides={totalSlides} 
        currentSlide={currentSlide}
        onSlideClick={handleSlideClick}
      />
    </div>
  );
};

export default TabletScreenshotCarousel;
